import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import BackToTopButton from '@/components/base/BackToTopButton';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useState, useRef } from 'react';

function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }

    formData.delete('phone_alt');

    setStatus('loading');
    setErrorMsg('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9nimt0bdkpk99j7gk50', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        setErrorMsg(serverMsg);
        setStatus('error');
      }
    } catch {
      setErrorMsg('网络错误，请检查网络连接后重试');
      setStatus('error');
    }
  };

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      data-readdy-form="true"
      className="space-y-3"
    >
      {status === 'success' && (
        <div className="p-3 rounded-lg bg-green-50 text-green-700 text-xs">
          留言已成功发送！我们会尽快回复您。
        </div>
      )}
      {status === 'error' && (
        <div className="p-3 rounded-lg bg-red-50 text-red-600 text-xs">
          {errorMsg}
        </div>
      )}
      <div>
        <input
          type="text"
          name="name"
          placeholder="您的姓名"
          className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
          required
        />
      </div>
      <div>
        <input
          type="email"
          name="email"
          placeholder="您的邮箱"
          className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
          required
        />
      </div>
      <div>
        <textarea
          name="message"
          placeholder="请输入您的留言内容（500字以内）"
          rows={4}
          maxLength={500}
          className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
          required
        ></textarea>
      </div>
      <input
        type="text"
        name="phone_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-anti-spam"
      />
      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full py-2.5 rounded-lg bg-primary-500 text-white font-semibold text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {status === 'loading' ? '发送中...' : '发送留言'}
      </button>
    </form>
  );
}

export default function AboutPage() {
  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '午马影院',
    url: 'https://wumacinema.com',
    description: '高清不缓冲免费在线影视平台，提供最新电影、热播剧集在线观看服务。',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-9999',
      contactType: 'customer service',
      availableLanguage: 'Chinese',
      email: 'contact@wumacinema.com',
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CN',
      addressLocality: '北京',
      addressRegion: '北京市',
      streetAddress: '朝阳区建国路88号',
    },
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '午马影院是什么？',
        acceptedAnswer: { '@type': 'Answer', text: '午马影院是一个高清免费在线影视平台，提供最新电影、热播剧集、综艺、动漫、纪录片等海量资源的在线观看服务。' },
      },
      {
        '@type': 'Question',
        name: '午马影院如何保证高清流畅？',
        acceptedAnswer: { '@type': 'Answer', text: '我们采用智能CDN加速和分布式服务器架构，确保4K超清画质下的流畅播放体验，真正做到高清不缓冲。' },
      },
      {
        '@type': 'Question',
        name: '午马影院的资源更新频率如何？',
        acceptedAnswer: { '@type': 'Answer', text: '每日同步更新最新院线电影和热播剧集，确保用户第一时间观看到最新内容。' },
      },
      {
        '@type': 'Question',
        name: '如何联系午马影院客服？',
        acceptedAnswer: { '@type': 'Answer', text: '您可以通过客服邮箱 contact@wumacinema.com 或客服热线 400-888-9999 联系我们，工作时间为9:00-21:00。' },
      },
    ],
  };

  const milestones = [
    { year: '2020', title: '平台创立', desc: '午马影院正式上线，致力于打造免费高清影视平台。' },
    { year: '2021', title: '用户突破百万', desc: '凭借优质内容和流畅体验，注册用户突破100万。' },
    { year: '2022', title: '4K全面升级', desc: '全站升级4K超清画质，引入智能CDN加速技术。' },
    { year: '2023', title: '内容生态完善', desc: '影视资源突破10万+，覆盖全品类内容矩阵。' },
    { year: '2024', title: '用户突破5000万', desc: '活跃用户突破5000万，成为领先的免费影视平台。' },
  ];

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(orgLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />

            <div className="mt-6 mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                关于午马影院
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                高清不缓冲免费在线看，让每个人都能享受极致观影体验
              </p>
            </div>

            <section className="mb-12 animate-fade-in-up">
              <div className="flex flex-col lg:flex-row gap-8 items-start">
                <div className="flex-1">
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4">
                    <a href="#brand-intro" id="brand-intro">品牌介绍</a>
                  </h2>
                  <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
                    <p>
                      午马影院成立于2020年，是国内领先的<strong>免费高清在线影视平台</strong>。我们始终秉持"让每个人都能享受优质影视内容"的理念，为用户提供<strong>高清不缓冲</strong>的观影体验。
                    </p>
                    <p>
                      平台汇聚了超过<strong>10万部影视资源</strong>，涵盖<strong>电影、电视剧、综艺、动漫、纪录片</strong>等全品类内容。无论是院线大片、热播剧集，还是经典老片、小众佳片，在午马影院都能找到。
                    </p>
                    <p>
                      我们采用先进的<strong>智能CDN加速技术</strong>和分布式服务器架构，确保在任何网络环境下都能实现<strong>即点即播、流畅不卡顿</strong>的播放体验。同时支持电脑、平板、手机等多终端观看，让您随时随地畅享高清影视。
                    </p>
                    <p>
                      午马影院<strong>完全免费，无需注册</strong>，打开浏览器即可观看。我们相信好的内容应该被更多人看到，这也是午马影院始终坚持的初心。
                    </p>
                  </div>
                </div>
                <div className="lg:w-[400px] flex-shrink-0">
                  <div className="aspect-video rounded-lg overflow-hidden bg-background-100">
                    <img
                      src="https://readdy.ai/api/search-image?query=A%20modern%20cozy%20home%20theater%20setup%20with%20large%20screen%20displaying%20cinematic%20content%2C%20warm%20ambient%20lighting%2C%20comfortable%20seating%2C%20minimalist%20interior%20design%2C%20premium%20home%20entertainment%20atmosphere%2C%20soft%20golden%20light&width=800&height=450&seq=about-cinema&orientation=landscape"
                      alt="午马影院 - 品牌形象"
                      title="午马影院品牌形象"
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-12 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4">
                <a href="#reputation" id="reputation">口碑声誉</a>
              </h2>
              <p className="text-sm text-foreground-500 mb-6">
                午马影院凭借优质的服务和内容，赢得了数千万用户的信赖与好评。
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {[
                  { num: '5000万+', label: '活跃用户', icon: 'ri-user-heart-line' },
                  { num: '10万+', label: '影片资源', icon: 'ri-movie-line' },
                  { num: '4.9/5.0', label: '用户评分', icon: 'ri-star-fill' },
                  { num: '99.9%', label: '播放成功率', icon: 'ri-check-double-line' },
                ].map((stat) => (
                  <div key={stat.label} className="p-5 rounded-xl bg-background-100 text-center">
                    <span className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-lg bg-accent-100 text-accent-500">
                      <i className={`${stat.icon} text-lg`}></i>
                    </span>
                    <div className="text-2xl font-heading font-bold text-foreground-950">{stat.num}</div>
                    <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { name: '张先生', text: '资源真的很全，更新也快，画质清晰流畅，观影体验非常好！不用注册就能看，太方便了。', role: '忠实用户 · 3年' },
                  { name: '李女士', text: '一直在用午马影院追剧，高清不缓冲名副其实，推荐给了很多朋友，大家都说好用。', role: 'VIP用户 · 2年' },
                  { name: '王同学', text: '作为学生党，午马影院免费高清真的太友好了，动漫资源也很多，强烈推荐！', role: '活跃用户 · 1年' },
                ].map((review) => (
                  <div key={review.name} className="p-5 rounded-xl bg-background-100">
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">{review.text}</p>
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-500">
                        {review.name[0]}
                      </span>
                      <div>
                        <div className="text-xs font-medium text-foreground-800">{review.name}</div>
                        <div className="text-[10px] text-foreground-400">{review.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-12 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4">
                <a href="#milestones" id="milestones">发展历程</a>
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 -translate-x-1/2 hidden md:block"></div>
                <div className="space-y-6">
                  {milestones.map((m, idx) => (
                    <div
                      key={m.year}
                      className={`flex flex-col md:flex-row gap-4 items-start ${
                        idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                        <div className="bg-background-100 rounded-lg p-4 inline-block max-w-md">
                          <span className="text-xs font-bold text-primary-500">{m.year}</span>
                          <h3 className="text-sm font-semibold text-foreground-900 mt-1">{m.title}</h3>
                          <p className="text-xs text-foreground-500 mt-1">{m.desc}</p>
                        </div>
                      </div>
                      <div className="hidden md:flex w-8 flex-shrink-0 justify-center">
                        <span className="w-3 h-3 rounded-full bg-primary-500 mt-6"></span>
                      </div>
                      <div className="flex-1 hidden md:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mb-12 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4">
                <a href="#contact" id="contact">联系我们</a>
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="p-6 rounded-xl bg-background-100 space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900">公司地址</h3>
                      <p className="text-xs text-foreground-500 mt-1">北京市朝阳区建国路88号SOHO现代城A座</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900">客服热线</h3>
                      <p className="text-xs text-foreground-500 mt-1">400-888-9999</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900">电子邮箱</h3>
                      <p className="text-xs text-foreground-500 mt-1">contact@wumacinema.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900">工作时间</h3>
                      <p className="text-xs text-foreground-500 mt-1">周一至周日 9:00 - 21:00</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-background-100">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-4">给我们留言</h3>
                  <ContactForm />
                </div>
              </div>
            </section>

            <section className="mb-12 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4">
                <a href="#faq" id="faq">常见问题</a>
              </h2>
              <div className="space-y-3">
                {[
                  { q: '午马影院需要注册吗？', a: '不需要注册，午马影院完全免费免注册，打开即可观看所有内容。我们致力于让每个人都能无障碍地享受优质影视。' },
                  { q: '午马影院的画质如何？', a: '午马影院提供4K超清画质，采用智能CDN加速和自适应码率技术，根据您的网络状况自动调整，确保最佳观影体验。' },
                  { q: '午马影院支持哪些设备？', a: '支持电脑、平板、手机等多终端观看，兼容主流浏览器。无论您使用什么设备，只需打开浏览器即可畅享高清影视。' },
                  { q: '午马影院的资源更新频率？', a: '我们每日同步更新最新院线电影和热播剧集，确保用户第一时间观看到最新内容。热门资源通常在24小时内完成更新。' },
                  { q: '遇到播放问题怎么办？', a: '如遇卡顿可尝试切换清晰度或刷新页面。如果问题持续，请联系客服邮箱 contact@wumacinema.com 或拨打客服热线 400-888-9999。' },
                ].map((faq, idx) => (
                  <details key={idx} className="group rounded-lg bg-background-100">
                    <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 list-none">
                      {faq.q}
                      <i className="ri-arrow-down-s-line transition-transform duration-300 group-open:rotate-180 text-foreground-400"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-500 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </section>
          </div>
        </main>

        <Footer />
        <BackToTopButton />
      </div>
    </>
  );
}